import { useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { Facebook, Globe, Instagram, Music2, Youtube } from "lucide-react";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { UrlInput } from "@shared-kernel/primary/shared/url-input/url-input";
import { Switch } from "@shared-kernel/primary/shared/shadcn/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared-kernel/primary/shared/shadcn/ui/alert-dialog";
import { FieldLimitCounter } from "@shared-kernel/primary/shared/field-limit-counter/field-limit-counter";
import { SpecializationsPicker } from "@shared-kernel/primary/shared/specializations-picker/specializations-picker";
import { LongTextarea } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import { LONG_TEXT_TALL_MIN_HEIGHT } from "@shared-kernel/primary/shared/long-text-field/long-text-rows";
import { DESCRIPTION_MAX_LENGTH, PEDAGOGICAL_EXPERIENCE_MIN_LENGTH } from "./teacher-public-profile.subform.schema";

interface Props {
  teachingVenuesSlot: ReactNode;
}

export const TeacherPublicProfileSubform = ({ teachingVenuesSlot }: Props) => {
  const {
    control,
    setValue,
    formState: { defaultValues },
  } = useFormContext();
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Visibilité</h3>
        <FormField
          control={control}
          name="isPublicProfileVisibleByTeacher"
          render={({ field }) => {
            const applyVisibilityChange = (checked: boolean) => setValue("isPublicProfileVisibleByTeacher", checked, { shouldDirty: true });

            // Only a profile that was already published (persisted as visible) can be de-referenced from the
            // public site and lose its Google ranking. A profile merely toggled on within this session — e.g.
            // during onboarding, where it was never live — carries no such risk, so skip the confirmation.
            const wasPubliclyVisible = Boolean(defaultValues?.["isPublicProfileVisibleByTeacher"]);

            const handleVisibilityChange = (checked: boolean) => {
              if (!checked && wasPubliclyVisible) {
                setConfirmingDeactivation(true);
                return;
              }
              applyVisibilityChange(checked);
            };

            return (
              <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-4">
                <div className="space-y-1">
                  <FormLabel>Profil public visible</FormLabel>
                  <p className="text-sm text-muted-foreground">
                    {field.value
                      ? "Votre profil est visible publiquement (consultable par les élèves)."
                      : "Votre profil n’est pas visible publiquement."}
                  </p>
                  <p className="text-xs italic text-muted-foreground">
                    À noter : une dizaine de coachings effectués et un retour sur votre enseignement sont requis avant publication de votre
                    profil sur notre site.
                  </p>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={handleVisibilityChange} data-testid="public-profile-visibility-switch" />
                </FormControl>

                <AlertDialog open={confirmingDeactivation} onOpenChange={setConfirmingDeactivation}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Masquer votre profil public ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Attention : votre profil est actuellement visible sur le site public. En le masquant, il sera retiré du site et vous
                        n'apparaîtrez plus en haut des résultats Google. La position que vous avez gagnée sur Google sera perdue et
                        repartira de zéro si vous republiez plus tard.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => applyVisibilityChange(false)}
                      >
                        Masquer quand même
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </FormItem>
            );
          }}
        />
        <FormField
          control={control}
          name="isEmailVisibleOnPublicProfile"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-1">
                <FormLabel>Afficher mon email sur ma fiche publique</FormLabel>
                <p className="text-sm text-muted-foreground">Permet aux élèves de vous contacter directement par email.</p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={checked => setValue("isEmailVisibleOnPublicProfile", checked, { shouldDirty: true })}
                  data-testid="email-visibility-switch"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </section>
      <FormField
        control={control}
        name="description"
        render={({ field }) => {
          const value = field.value ?? "";
          return (
            <FormItem>
              <div className="space-x-2">
                <FormLabel>Présentation en une phrase</FormLabel>
                <FieldLimitCounter
                  current={value.length}
                  threshold={DESCRIPTION_MAX_LENGTH}
                  mode="max"
                  data-testid="public-profile-description-counter"
                />
              </div>
              <FormDescription>
                Affichée sous votre nom sur votre profil public. C'est la première chose qu'un élève lit — quelques mots qui vous situent en
                particulier.
              </FormDescription>
              <FormMessage />
              <FormControl>
                <Input {...field} value={value} maxLength={DESCRIPTION_MAX_LENGTH} data-testid="public-profile-description" />
              </FormControl>
            </FormItem>
          );
        }}
      />
      <FormField
        control={control}
        name="pedagogicalExperience"
        render={({ field }) => {
          const textLength = (field.value as string | undefined)?.trim().length ?? 0;
          return (
            <FormItem>
              <div className="space-x-2">
                <FormLabel>Mon expérience pédagogique</FormLabel>
                <FieldLimitCounter
                  current={textLength}
                  threshold={PEDAGOGICAL_EXPERIENCE_MIN_LENGTH}
                  mode="min"
                  suffix="caractères minimum"
                  data-testid="public-profile-pedagogical-experience-counter"
                />
              </div>
              <FormDescription>
                Affichée sous votre présentation. Votre parcours d'enseignant — établissements, master classes, collaborations marquantes,
                participations à des jurys, …
                <br />
                <br />
                Remarque : Vos approches et méthodologies se renseignent dans l'onglet «&nbsp;Ma pédagogie&nbsp;».
              </FormDescription>
              <FormControl>
                <LongTextarea field={field} className={LONG_TEXT_TALL_MIN_HEIGHT} data-testid="public-profile-pedagogical-experience" />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <SpecializationsPicker name="specializations" />
      {teachingVenuesSlot}
      <FormItem>
        <FormLabel>Présence en ligne (optionnel)</FormLabel>
        <FormDescription>
          Liens affichés sur votre profil public. Copiez l'adresse depuis la barre de votre navigateur, ou tapez-la comme dans les exemples.
        </FormDescription>
        <div className="space-y-3 rounded-md border p-3">
          <FormField
            control={control}
            name="websiteUrl"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <UrlInput
                    {...field}
                    icon={<Globe />}
                    label="Site perso"
                    placeholder="exemple.com"
                    data-testid="public-profile-website-url"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="instagramUrl"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <UrlInput
                    {...field}
                    kind="social"
                    icon={<Instagram />}
                    label="Instagram"
                    placeholder="instagram.com/votre-profil"
                    data-testid="public-profile-instagram-url"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="youtubeUrl"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <UrlInput
                    {...field}
                    kind="social"
                    icon={<Youtube />}
                    label="YouTube"
                    placeholder="youtube.com/@votre-chaine"
                    data-testid="public-profile-youtube-url"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="tiktokUrl"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <UrlInput
                    {...field}
                    kind="social"
                    icon={<Music2 />}
                    label="TikTok"
                    placeholder="tiktok.com/@votre-compte"
                    data-testid="public-profile-tiktok-url"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="facebookUrl"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <UrlInput
                    {...field}
                    kind="social"
                    icon={<Facebook />}
                    label="Facebook"
                    placeholder="facebook.com/votre-page"
                    data-testid="public-profile-facebook-url"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </FormItem>
    </div>
  );
};
